import { CreditCardProductCategory } from '@appTypes/EligibilityApi.interface'

type Cons = {
    heading: string
    text: string
}

export const consList: Record<Partial<CreditCardProductCategory>, Cons[]> = {
    [CreditCardProductCategory.BalanceTransfer]: [
        {
            heading: 'Transfer fees apply',
            text: 'Most cards will charge a processing fee of 2-4% to move your balance, although this is usually much less than you’ll save in interest.',
        },

        {
            heading: 'Need good credit',
            text: 'The best deals are reserved for customers with good to excellent credit scores – but there are offers available to those with poor credit ratings.',
        },

        {
            heading: 'High rates after the offer',
            text: 'Once the 0% period ends, rates can jump to 20-40%, so you make a clear repayment plan, and try and stick to it.',
        },

        {
            heading: 'Temptation to spend more',
            text: 'Having a clear balance on your old cards might tempt you to rack up more debt, so it can be best to close those accounts, and focus on clearing your debt.',
        },

        {
            heading: 'No purchase perks',
            text: 'These cards are designed to help you pay off your debts, and won’t usually come with any rewards or cashback.',
        },
    ],

    [CreditCardProductCategory.Cashback]: [
        {
            heading: 'Only works if you clear it monthly',
            text: 'If you miss a payment, the interest charges are likely to cost you more than the cashback you earn.',
        },

        {
            heading: 'Annual fees on some cards',
            text: 'Some of the more competitive cashback cards often charge a yearly fee, so work out if you’ll earn enough to cover any costs.',
        },

        {
            heading: 'Lower rates after introductory periods',
            text: 'Banks usually offer better incentives for the first few months, and then drop them to a lower ongoing rate.',
        },

        {
            heading: 'Best rates need good credit',
            text: 'The best cards with the best cashback offers are usually only available to people with the best credit scores.',
        },

        {
            heading: 'Cashback is often capped',
            text: 'Many cards will put a limit how much you can earn, so check the terms.',
        },

        {
            heading: 'Can encourage overspending',
            text: 'A cashback card could tempt you to spend more just to earn rewards.',
        },
    ],

    [CreditCardProductCategory.Combined]: [
        {
            heading: 'Different 0% periods',
            text: 'The interest-free time for transfers and purchases will usually be different, so set a reminder to make sure you don’t get caught out.',
        },

        {
            heading: 'Balance transfer fees',
            text: 'Remember that balance transfers usually come with a small one-off fee of 2-4%.',
        },

        {
            heading: 'Shorter offers than specialist cards',
            text: 'Combo cards usually have shorter 0% periods than cards that focus on just balance transfers or purchases.',
        },

        {
            heading: 'Can be tricky to manage',
            text: 'Keeping track of two different promotional periods and making sure you clear both before they end takes planning.',
        },

        {
            heading: 'Temptation to overspend',
            text: 'Having the option to both clear debt and make purchases might encourage you to spend more than you should.',
        },

        {
            heading: 'Limited options',
            text: 'Not as many providers offer dual credit cards, and most lenders won’t let you transfer a balance from the same bank.',
        },
    ],

    [CreditCardProductCategory.PoorCredit]: [
        {
            heading: 'Higher interest rates',
            text: 'These cards can come with higher APRs which might range from 20% to 60%, making it an expensive way to borrow if you don’t pay off the balance in full.',
        },

        {
            heading: 'Low credit limits',
            text: 'Limits might typically start at £200-£1,500, which might not cover larger purchases.',
        },

        {
            heading: 'Annual fees',
            text: 'Some cards for bad credit might charge yearly fees, adding to the cost even if you don’t use the card much.',
        },

        {
            heading: 'Temptation to overspend',
            text: 'Having credit available to you might encourage poor spending habits if you don’t keep to the plan.',
        },

        {
            heading: 'Limited perks',
            text: 'These cards are no-frills, and are unlikely to come with cashback, rewards, or 0% offers.',
        },
    ],

    [CreditCardProductCategory.Purchase]: [
        {
            heading: 'High rates after the offer',
            text: 'As soon as the 0% period ends, rates will suddenly jump – so make a repayment plan and try your best to stick to it.',
        },

        {
            heading: 'Need good credit',
            text: 'The longest 0% offers are usually reserved for people with the best credit scores – so check your eligibility before applying.',
        },

        {
            heading: 'Temptation to overspend',
            text: 'Having credit available to you might encourage you to buy things you don’t really need, so try your best to stay within budget.',
        },

        {
            heading: 'Minimum payments only cover interest later',
            text: 'Once the 0% period ends, minimum payments might barely cover the interest charges, making it harder to clear the balance.',
        },

        {
            heading: 'No cash withdrawal benefits',
            text: 'These cards are made for purchases, and withdrawing cash with a credit card will usually mean extra charges and higher interest rates.',
        },
    ],

    [CreditCardProductCategory.Rewards]: [
        {
            heading: 'Only works if you clear it monthly',
            text: 'Miss a payment or carry a balance, and the interest charges can cost you more than the rewards you’re earning.',
        },

        {
            heading: 'Annual fees on some cards',
            text: 'Premium rewards cards often come with a yearly fee, so you’ll need to earn enough rewards to justify the cost.',
        },

        {
            heading: 'Can encourage overspending',
            text: 'Rewards cards can sometimes tempt you to spend more just to earn points – so be careful not to fall into the trap.',
        },

        {
            heading: 'Rewards can expire',
            text: 'Unlike cashback, rewards often have expiry dates, so you’ll need to keep track and use them before they go.',
        },

        {
            heading: 'Best cards need good credit',
            text: 'The cards with the most generous rewards will usually only be available to people with the best credit scores.',
        },

        {
            heading: 'Limited where you can use rewards',
            text: 'Some rewards can only be spent with certain retailers or at specific times, so make sure your card matches your lifestyle.',
        },
    ],

    [CreditCardProductCategory.Travel]: [
        {
            heading: 'Higher APRs than other cards',
            text: 'Travel cards often come with higher interest rates, making them expensive if you carry a balance.',
        },

        {
            heading: 'No 0% offers',
            text: "Unlike purchase cards, most travel cards won't give you interest-free periods on spending.",
        },

        {
            heading: 'Interest on cash withdrawals',
            text: 'Even cards with no ATM fees are likely to start charging interest when you withdraw cash.',
        },

        {
            heading: 'Annual fees on some cards',
            text: "The best travel cards might charge a yearly fee, so check if you'll use it enough to justify the cost.",
        },

        {
            heading: 'Limited acceptance',
            text: "Some cards aren't accepted everywhere, so you might need a backup card and cash.",
        },

        {
            heading: 'Not great for UK spending',
            text: "These cards are designed for travel, so they're usually not the best choice for everyday UK purchases.",
        },
    ],

    // TODO: Add cons for other categories when the data is ready
    [CreditCardProductCategory.BestCards]: [],
}
